import * as React from 'react';
import { Box, Typography, Chip, Skeleton, IconButton, Tooltip, Divider } from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LabelIcon from '@mui/icons-material/Label';
import SummarizeIcon from '@mui/icons-material/Summarize';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SummaryApi from '../../api/SummaryApi';

// 格式化毫秒为可读时间
const formatTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// 格式化时间范围
const formatTimeRange = (startMs, endMs) => {
    return `${formatTime(startMs)} - ${formatTime(endMs)}`;
};

// 话题卡片组件
const TopicCard = ({ topic, index, onSeek }) => {
    return (
        <Box 
            sx={{ 
                position: 'relative',
                pl: 3,
                pb: 3,
                '&:last-child': { pb: 0 },
                // 时间轴线
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: '7px',
                    top: '24px',
                    bottom: 0,
                    width: '2px',
                    bgcolor: 'divider',
                },
                '&:last-child::before': {
                    display: 'none',
                },
            }}
        >
            {/* 时间轴节点 */}
            <Box 
                sx={{ 
                    position: 'absolute',
                    left: 0,
                    top: '6px',
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    border: '3px solid',
                    borderColor: 'background.paper',
                    boxShadow: '0 0 0 2px rgba(255, 139, 147, 0.2)',
                    zIndex: 1,
                }}
            />
            
            {/* 话题内容 */}
            <Box 
                sx={{ 
                    bgcolor: 'background.paper',
                    borderRadius: '12px',
                    border: '1px solid',
                    borderColor: 'divider',
                    overflow: 'hidden',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        borderColor: 'primary.light',
                        boxShadow: '0 2px 12px rgba(255, 139, 147, 0.1)',
                    },
                }}
            >
                {/* 话题头部 */}
                <Box 
                    sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2,
                        py: 1.5,
                        bgcolor: 'rgba(255, 139, 147, 0.04)',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                        <Chip 
                            label={`#${index + 1}`}
                            size="small"
                            sx={{ 
                                height: '22px',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                bgcolor: 'primary.main',
                                color: 'white',
                                '& .MuiChip-label': { px: 1 },
                            }}
                        />
                        <Typography 
                            variant="subtitle2" 
                            sx={{ 
                                fontWeight: 600,
                                color: 'text.primary',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {topic.title}
                        </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                        <Tooltip title="跳转到此时间点">
                            <IconButton 
                                size="small" 
                                onClick={() => onSeek(topic.startTime / 1000)}
                                sx={{ 
                                    color: 'text.secondary',
                                    '&:hover': { color: 'primary.main', bgcolor: 'rgba(255, 139, 147, 0.08)' },
                                }}
                            >
                                <PlayCircleOutlineIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Box 
                            sx={{ 
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                px: 1,
                                py: 0.25,
                                bgcolor: 'rgba(0, 0, 0, 0.04)',
                                borderRadius: '6px',
                            }}
                        >
                            <AccessTimeIcon sx={{ fontSize: '0.875rem', color: 'text.secondary' }} />
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    fontFamily: 'monospace',
                                    fontWeight: 500,
                                    color: 'text.secondary',
                                }}
                            >
                                {formatTimeRange(topic.startTime, topic.endTime)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
                
                {/* 话题摘要 */}
                <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            color: 'text.secondary',
                            lineHeight: 1.7,
                            fontSize: '0.8125rem',
                        }}
                    >
                        {topic.summary}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

// 加载骨架屏
const SummarySkeleton = () => (
    <Box sx={{ p: 2 }}>
        <Skeleton variant="text" width="40%" height={28} sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
            {[1, 2, 3, 4].map(i => (
                <Skeleton key={i} variant="rounded" width={60} height={24} />
            ))}
        </Box>
        <Skeleton variant="rounded" height={80} sx={{ mb: 3 }} />
        {[1, 2, 3].map(i => (
            <Box key={i} sx={{ pl: 3, mb: 2 }}>
                <Skeleton variant="rounded" height={120} />
            </Box>
        ))}
    </Box>
);

// 空状态组件
const EmptyState = () => (
    <Box 
        sx={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 6,
            px: 3,
            textAlign: 'center',
        }}
    >
        <AutoAwesomeIcon 
            sx={{ 
                fontSize: '3rem',
                color: 'text.disabled',
                mb: 2,
            }} 
        />
        <Typography 
            variant="subtitle1" 
            sx={{ 
                color: 'text.secondary',
                fontWeight: 500,
                mb: 0.5,
            }}
        >
            暂无 AI 总结
        </Typography>
        <Typography 
            variant="body2" 
            sx={{ 
                color: 'text.disabled',
            }}
        >
            该视频尚未生成 AI 内容总结
        </Typography>
    </Box>
);

export const AISummaryPanel = ({ clipId, onSeek }) => {
    const [summary, setSummary] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);

    React.useEffect(() => {
        let isMounted = true; // 防止组件卸载后更新状态
        
        const fetchSummary = async () => {
            if (!clipId) return;
            
            setLoading(true);
            setError(null);
            
            try {
                const data = await SummaryApi.findByClipId(clipId);
                if (isMounted) {
                    setSummary(data);
                }
            } catch (ex) {
                if (!isMounted) return;
                
                // 检查是否是 "未找到总结" 的错误
                // 后端返回格式: { code: 400100150, message: 'AI 总结不存在' }
                const isNotFoundError = 
                    ex.code === 400100150 ||  // 后端 error.summary.NotFound 错误码
                    ex.message?.includes('AI 总结不存在') ||
                    ex.message?.includes('not found');
                
                if (isNotFoundError) {
                    setSummary(null);
                } else {
                    setError(ex.message || '获取总结失败');
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchSummary();
        
        return () => {
            isMounted = false;
        };
    }, [clipId]); // 移除 onMessage 依赖，避免无限循环

    if (loading) {
        return <SummarySkeleton />;
    }

    if (error) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    if (!summary) {
        return <EmptyState />;
    }

    return (
        <Box 
            sx={{ 
                height: '100%',
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                    width: '6px',
                },
                '&::-webkit-scrollbar-thumb': {
                    bgcolor: 'rgba(0,0,0,0.1)',
                    borderRadius: '3px',
                },
            }}
        >
            {/* 关键词区域 */}
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <LabelIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        关键词
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {summary.keywords.map((keyword, index) => (
                        <Chip
                            key={index}
                            label={keyword}
                            size="small"
                            variant="outlined"
                            sx={{
                                height: '26px',
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                borderColor: 'primary.light',
                                color: 'primary.dark',
                                bgcolor: 'rgba(255, 139, 147, 0.06)',
                                '&:hover': {
                                    bgcolor: 'rgba(255, 139, 147, 0.12)',
                                },
                            }}
                        />
                    ))}
                </Box>
            </Box>

            {/* 整体概述区域 */}
            <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <SummarizeIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        内容概述
                    </Typography>
                </Box>
                <Typography 
                    variant="body2" 
                    sx={{ 
                        color: 'text.secondary',
                        lineHeight: 1.8,
                        fontSize: '0.8125rem',
                        bgcolor: 'rgba(0, 0, 0, 0.02)',
                        p: 1.5,
                        borderRadius: '8px',
                        border: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    {summary.overallSummary}
                </Typography>
            </Box>

            {/* 话题时间轴 */}
            <Box sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <AccessTimeIcon sx={{ fontSize: '1rem', color: 'primary.main' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        话题时间轴
                    </Typography>
                    <Chip 
                        label={`${summary.topics.length} 个话题`}
                        size="small"
                        sx={{
                            height: '20px',
                            fontSize: '0.6875rem',
                            bgcolor: 'rgba(0, 0, 0, 0.06)',
                            '& .MuiChip-label': { px: 1 },
                        }}
                    />
                </Box>
                
                {/* 时间轴内容 */}
                <Box sx={{ mt: 2 }}>
                    {summary.topics.map((topic, index) => (
                        <TopicCard 
                            key={index}
                            topic={topic}
                            index={index}
                            onSeek={onSeek}
                        />
                    ))}
                </Box>
            </Box>

            {/* 底部信息 */}
            <Divider />
            <Box 
                sx={{ 
                    px: 2,
                    py: 1.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'rgba(0, 0, 0, 0.02)',
                }}
            >
                <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                    由 AI 自动生成 · {summary.model}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                    {new Date(summary.createdAt).toLocaleDateString('zh-CN')}
                </Typography>
            </Box>
        </Box>
    );
};
